import React, { useState, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Container,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import { appConstants } from '../_constants/app.constants';
import { useAuth } from '../auth/AuthContext';

const pad = (n) => String(n).padStart(2, '0');

function toDateTimeLocalString(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Mañana a las 10:00 (hora local) — atajo cómodo al crear */
function tomorrowTenAmLocal() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  return toDateTimeLocalString(d);
}

function defaultDateTimeLocal() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(10);
  return toDateTimeLocalString(d);
}

export default function CreateTournamentPage() {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dayLocal, setDayLocal] = useState(defaultDateTimeLocal);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = useMemo(() => name.trim().length > 0 && !submitting, [name, submitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }
    setSubmitting(true);
    try {
      const day = new Date(dayLocal);
      if (Number.isNaN(day.getTime())) {
        setError('Fecha u hora no válida.');
        setSubmitting(false);
        return;
      }
      const { data } = await axios.post(`${appConstants.API_URL}/api/tournaments`, {
        name: name.trim(),
        description: (description || '').trim(),
        // wall-clock local: enviamos el string tal cual del datetime-local
        // (ISO sin Z/offset). NO usar .toISOString(): convierte a UTC.
        day: dayLocal,
        clubId: null,
      });
      navigate(`/tournamentactions/${data.id}/setup`, { replace: true });
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.Message ||
        err.response?.data?.errors?.title ||
        err.message ||
        'No se pudo crear el torneo.';
      setError(typeof msg === 'string' ? msg : 'Error al crear el torneo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (ready && !user) {
    return (
      <Container maxWidth="sm" className="py-10">
        <Alert severity="info" sx={{ mb: 2 }}>
          Inicia sesión para crear un torneo.
        </Alert>
        <Button component={RouterLink} to="/login" variant="contained">
          Ir a iniciar sesión
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" className="py-6 sm:py-10 px-3">
      <Button
        component={RouterLink}
        to="/mis-torneos"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        Volver a mis torneos
      </Button>

      <Paper
        elevation={0}
        className="rounded-3xl border border-arena/10 shadow-card overflow-hidden"
        sx={{ p: { xs: 2.5, sm: 4 } }}
      >
        <div className="h-1 -mx-4 -mt-4 mb-6 sm:-mx-8 sm:-mt-8 bg-gradient-to-r from-gold via-gold-bright to-arena-bright rounded-t-3xl" />
        <Typography variant="h5" className="!font-display !font-extrabold !text-ink mb-1">
          Crear torneo
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Solo necesitas un nombre y cuándo empieza. Podrás añadir equipos, grupos y partidos después.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <TextField
              label="Nombre del torneo"
              required
              fullWidth
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej. Torneo verano 2026"
              inputProps={{ maxLength: 200 }}
            />
            <TextField
              label="Descripción (opcional)"
              fullWidth
              multiline
              minRows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Normas, categoría, sede…"
            />
            <TextField
              label="Fecha y hora de inicio"
              type="datetime-local"
              fullWidth
              value={dayLocal}
              onChange={(e) => setDayLocal(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button type="button" size="small" variant="outlined" onClick={() => setDayLocal(tomorrowTenAmLocal())}>
                Mañana a las 10:00
              </Button>
              <Button type="button" size="small" variant="text" onClick={() => setDayLocal(defaultDateTimeLocal())}>
                Hoy a las 10:00
              </Button>
            </Stack>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={!canSubmit}
              startIcon={<AddIcon />}
              className="!mt-2 !py-3 !rounded-xl !font-bold !bg-btn-arena !shadow-lg"
            >
              {submitting ? 'Creando…' : 'Crear torneo'}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
}
